import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  AuthResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  LogoutResponse,
  ApiResponse,
  ApiListResponse,
  ErrorResponse
} from '@/types'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Interceptor para tratamento de erros
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response?.data) {
          const errorData = error.response.data as ErrorResponse
          console.error('API Error:', errorData.error)
        }
        return Promise.reject(error)
      }
    )
  }

  // Métodos genéricos para CRUD
  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, { params })
    return response.data
  }

  async getList<T>(url: string, params?: Record<string, any>): Promise<ApiListResponse<T>> {
    const response = await this.api.get<ApiListResponse<T>>(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url, data)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url)
    return response.data
  }

  // Upload de arquivos
  async uploadFile(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<any>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.api.post<ApiResponse<any>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })

    return response.data
  }

  // Métodos de autenticação
  setAuthToken(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  removeAuthToken() {
    delete this.api.defaults.headers.common['Authorization']
  }
}

export default new ApiService()

// Instância da API para autenticação
export const apiInstance = axios.create({
  baseURL: 'https://api.2pj.com.br/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para adicionar token automaticamente
apiInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para tratar erros de autenticação
apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Métodos de Autenticação
export const authService = {
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await apiInstance.post('/auth/login', data)
    return response.data
  },

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response: AxiosResponse<RegisterResponse> = await apiInstance.post('/auth/register', data)
    return response.data
  },

  async getMe(): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await apiInstance.get('/auth/me')
    return response.data
  },

  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const response: AxiosResponse<ChangePasswordResponse> = await apiInstance.post('/auth/change-password', data)
    return response.data
  },

  async logout(): Promise<LogoutResponse> {
    const response: AxiosResponse<LogoutResponse> = await apiInstance.post('/auth/logout')
    return response.data
  },

  // Método para configurar token manualmente
  setAuthToken(token: string) {
    apiInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`
  },

  // Método para remover token
  removeAuthToken() {
    delete apiInstance.defaults.headers.common['Authorization']
  }
} 